'use client';

import React, { useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCrosshairs } from '@fortawesome/pro-solid-svg-icons';
import type {
  UserLocation,
  GeolocationPermission,
} from 'src/hooks/geolocation/useUserLocation';

interface MyLocationButtonProps {
  userLocation: UserLocation | null;
  isLocating: boolean;
  permission: GeolocationPermission;
  onRequestLocation: () => Promise<UserLocation | null>;
  onClearLocation: () => void;
  onLocationAcquired?: (location: UserLocation) => void;
}

const MyLocationButton = ({
  userLocation,
  isLocating,
  permission,
  onRequestLocation,
  onClearLocation,
  onLocationAcquired,
}: MyLocationButtonProps) => {
  const handleClick = useCallback(async () => {
    if (userLocation) {
      onClearLocation();
      return;
    }

    const location = await onRequestLocation();
    if (location) {
      onLocationAcquired?.(location);
    }
  }, [userLocation, onRequestLocation, onClearLocation, onLocationAcquired]);

  const isActive = !!userLocation;
  const isDisabled =
    permission === 'denied' || permission === 'unavailable';

  return (
    <div className="absolute bottom-5 right-5 z-40">
      <div className="bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 inline-flex flex-col overflow-hidden">
        <button
          onClick={handleClick}
          disabled={isDisabled || isLocating}
          aria-label={isActive ? 'Hide my location' : 'Show my location'}
          className={`font-semibold transition-all duration-200 select-none text-base
            active:bg-neutral-200 p-4 flex items-center justify-center rounded-none
            disabled:bg-neutral-100 disabled:text-neutral-400
            ${
              isActive
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
            }
          `}
          data-testid="my-location-button"
        >
          <Icon
            icon={faCrosshairs}
            className={`font-bold ${isLocating ? 'animate-pulse' : ''}`}
          />
        </button>
      </div>
    </div>
  );
};

export default MyLocationButton;
